import { IFormSchemaV2 } from "@graffer-inc/form-schema";

import { isReplacedApplicantCorporateSearchComponent } from "../../domain/procedure/form-schema/applicant-schema";
import { SmartApplyFormSchemaValidator } from "../../domain/procedure/form-schema/smart-apply-form-schema-validator";
import { MigrationError } from "../error";
import { CloudGovFormSchema } from "../../domain/procedure/form-schema/cloudgov-form-schema";
import { hasIgnorableValidationErrorsOnly } from "../util";
import { DefaultApplicantSchemaKeys } from "../../domain/procedure/procedure-detail";

type Response = {
  migrated?: IFormSchemaV2;
  migrationError?: MigrationError;
};

export const down = (formSchema: IFormSchemaV2): Response => {
  if (!isValidSchemaToStartMigration(formSchema)) {
    return {
      migrationError: new MigrationError(
        "VALIDATION_ERROR_BEFORE_MIGRATION",
        new SmartApplyFormSchemaValidator(
          formSchema,
          DefaultApplicantSchemaKeys,
        ).validateDeep(),
      ),
    };
  }

  if (
    !formSchema.root[0].children.some((element) =>
      isReplacedApplicantCorporateSearchComponent(
        element,
        DefaultApplicantSchemaKeys,
      ),
    )
  ) {
    return {
      migrationError: new MigrationError("MIGRATION_UNNECESSARY", [
        "法人検索コンポーネントが存在しないため、マイグレーションの必要がありません",
      ]),
    };
  }

  // up と同様に、 CloudGovFormSchema の constructor を介す。
  const migrated: IFormSchemaV2 = JSON.parse(
    JSON.stringify(
      new CloudGovFormSchema({
        ...formSchema,
        root: formSchema.root.map((section, index) => {
          if (index === 0) {
            return {
              ...section,
              children: section.children.filter(
                ({ id }) =>
                  id !== DefaultApplicantSchemaKeys.applicantCorporateSearch.id,
              ),
            };
          }
          return section;
        }),
        conditionalRuleSet: {
          ...formSchema.conditionalRuleSet,
          conditionalRules: formSchema.conditionalRuleSet!.conditionalRules.map(
            (rule) => {
              return {
                ...rule,
                then: {
                  ...rule.then,
                  rules: rule.then.rules.filter(
                    ({ id }) =>
                      id !==
                      DefaultApplicantSchemaKeys.applicantCorporateSearch.id,
                  ),
                },
              };
            },
          ),
        },
      }),
    ),
  );

  if (!isValidSchemaToFinishMigration(migrated)) {
    return {
      migrated,
      migrationError: new MigrationError(
        "VALIDATION_ERROR_AFTER_MIGRATION",
        new SmartApplyFormSchemaValidator(
          migrated,
          DefaultApplicantSchemaKeys,
        ).validateDeep(),
      ),
    };
  }

  return { migrated };
};

const isValidSchemaToStartMigration = (formSchema: IFormSchemaV2): boolean => {
  const validator = new SmartApplyFormSchemaValidator(
    formSchema,
    DefaultApplicantSchemaKeys,
  );
  if (validator.isValid()) {
    return true;
  }

  if (hasIgnorableValidationErrorsOnly(validator)) {
    return true;
  }

  const validationErrors = validator.validateDeep();

  // migrate down することで、結果的にパスできる（可能性がある）ため、
  // totalFieldElementCount() によるエラーはいったん見逃す
  if (
    validationErrors.length === 1 &&
    validationErrors[0] === validator.totalFieldElementCount()
  ) {
    return true;
  }

  return false;
};

const isValidSchemaToFinishMigration = (formSchema: IFormSchemaV2): boolean => {
  const validator = new SmartApplyFormSchemaValidator(
    formSchema,
    DefaultApplicantSchemaKeys,
  );
  if (validator.isValid()) {
    return true;
  }

  return hasIgnorableValidationErrorsOnly(validator);
};
